/**
 * FaceReviewApprovals — the reviewer surface for clock-in face-mismatch punches.
 *
 * Closes the loop that shipped half-open: when face matching is set to the
 * `pending_review` fail policy and a worker's clock-in selfie doesn't match
 * their enrolled template, the punch is recorded as pending_review and an
 * ApprovalRequest(type='clock_in_face_review') is created + the manager/admins
 * are notified. But there was NO discoverable reviewer entry point — the
 * approve/reject lived only inside the Time Entries detail dialog, and the
 * notification deep-linked to a screen that didn't list it. Owner report
 * 2026-06-17: "I log in as admin and there's no reminder and no entry point."
 *
 * This page lists pending (and decided) face reviews with the captured selfie,
 * the similarity score, and Approve / Reject (with reason). Backed by:
 *   GET   /v1/attendance/approvals?type=clock_in_face_review[&status=]
 *   PATCH /v1/attendance/approvals/{id}   { status, note }
 * Approve → punch verified; Reject → punch voided (face_failed) + audit edit
 * (handled server-side in _apply_clock_in_face_review_decision).
 *
 * Chrome follows svdy-ui §1 (§13 page frame), §3 (inline stats), §7 (empty
 * state). Selfie is presigned on read by list_approvals.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Avatar, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Paper, Snackbar, Stack, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, ToggleButton,
  ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import {
  CheckCircleOutlined, CloseOutlined, FaceOutlined, RefreshOutlined,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { ApiError, attendanceApi } from '../../../api';
import type { Approval, ApprovalStatus } from '../../../api/attendance';
import PhotoViewerDialog from '../../../components/PhotoViewerDialog';
import { activeLocale } from '../../../utils/datetime';
import { translateApiError } from '../../../i18n/translateApiError';
import { seedStatusFromUrl } from '../../../utils/seedStatusFromUrl';

interface FaceReviewData {
  time_entry_id?: string;
  date?: string;
  similarity?: number;
  selfie_url?: string;
}

const FaceReviewApprovals: React.FC = () => {
  const { t } = useTranslation();

  const [rows, setRows] = useState<Approval[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // BUG-4 fix (2026-07-07): a manager who isn't also an admin (no
  // attendance.approvals.manage permission) can land here from the
  // clock_in_face_review push/bell notification and hit a bare 403.
  // Mirrors the mobile face-reviews.tsx posture ("Admin-only... a
  // non-admin gets a friendly no-access state") — same backend RBAC gate,
  // same reusable /v1/attendance/approvals endpoint, just a nicer render.
  const [forbidden, setForbidden] = useState(false);
  // Seeded from `?status=` so a count that links here lands on what it counted — see
  // src/utils/seedStatusFromUrl.ts. This page is mounted in BOTH shells (the Attendance module and, since
  // 2026-09-04, the worker portal), so the seeding belongs here rather than at either call site.
  const [statusFilter, setStatusFilter] = useState<ApprovalStatus | 'all'>(
    () => seedStatusFromUrl<ApprovalStatus | 'all'>('pending'),
  );
  const [acting, setActing] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  // Reject-with-reason dialog state.
  const [rejectId, setRejectId] = useState<string | null>(null);
  const [rejectNote, setRejectNote] = useState('');

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError(null);
    setForbidden(false);
    try {
      const data = await attendanceApi.approvalsApi.list({
        type: 'clock_in_face_review',
        status: statusFilter === 'all' ? undefined : statusFilter,
      });
      setRows(data);
    } catch (e) {
      if (e instanceof ApiError && e.status === 403) {
        setForbidden(true);
      } else {
        setError(e instanceof ApiError ? translateApiError(e, t) : t('attendance:faceReviewApprovals.loadFailed'));
      }
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, t]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const stats = useMemo(() => {
    let pending = 0; let approved = 0; let rejected = 0;
    for (const r of rows) {
      if (r.status === 'pending') pending += 1;
      else if (r.status === 'approved') approved += 1;
      else if (r.status === 'rejected') rejected += 1;
    }
    return { pending, approved, rejected };
  }, [rows]);

  const decide = async (id: string, status: 'approved' | 'rejected', note?: string) => {
    setActing(id);
    try {
      await attendanceApi.approvalsApi.update(id, { status, note: note ?? null });
      setToast(t(status === 'approved'
        ? 'attendance:faceReviewApprovals.approved'
        : 'attendance:faceReviewApprovals.rejected'));
      await fetchRows();
    } catch (e) {
      setToast(e instanceof ApiError ? translateApiError(e, t) : t('attendance:faceReviewApprovals.actionFailed'));
    } finally {
      setActing(null);
      setRejectId(null);
      setRejectNote('');
    }
  };

  const statusChip = (s: string) => {
    const map: Record<string, { color: 'warning' | 'success' | 'error'; key: string }> = {
      pending: { color: 'warning', key: 'statusPending' },
      approved: { color: 'success', key: 'statusApproved' },
      rejected: { color: 'error', key: 'statusRejected' },
    };
    const m = map[s] ?? { color: 'warning' as const, key: 'statusPending' };
    return (
      <Chip size="small" variant="outlined" color={m.color}
        label={t(`attendance:faceReviewApprovals.${m.key}`)}
        sx={{ borderRadius: 1, height: 20, fontSize: '0.65rem' }} />
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', py: '12px', width: '100%' }}>
      {forbidden ? (
        <Paper elevation={0} sx={{ width: '100%', p: 5, textAlign: 'center', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
          <FaceOutlined fontSize="medium" sx={{ color: 'text.disabled', mb: 1 }} />
          <Typography variant="body2" color="text.secondary">
            {t('attendance:faceReviewApprovals.noAccessTitle')}
          </Typography>
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
            {t('attendance:faceReviewApprovals.noAccessHint')}
          </Typography>
        </Paper>
      ) : (
        <>
      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Box>
          <Stack direction="row" alignItems="center" spacing={1}>
            <FaceOutlined fontSize="small" sx={{ color: 'primary.main' }} />
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem', color: 'text.primary' }}>
              {t('attendance:faceReviewApprovals.title')}
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {t('attendance:faceReviewApprovals.subtitle')}
          </Typography>
        </Box>
        <Tooltip title={t('common:actions.refresh')}>
          <Button size="small" variant="outlined" onClick={fetchRows} startIcon={<RefreshOutlined fontSize="small" />}
            sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.8rem', color: 'text.primary', borderColor: 'rgba(0,0,0,0.2)' }}>
            {t('common:actions.refresh')}
          </Button>
        </Tooltip>
      </Box>

      {/* §13 inline stats */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        <strong>{stats.pending}</strong> {t('attendance:faceReviewApprovals.statPending')} ·{' '}
        <strong>{stats.approved}</strong> {t('attendance:faceReviewApprovals.statApproved')} ·{' '}
        <strong>{stats.rejected}</strong> {t('attendance:faceReviewApprovals.statRejected')}
      </Typography>

      <ToggleButtonGroup size="small" exclusive value={statusFilter}
        onChange={(_, v) => v && setStatusFilter(v)} sx={{ mb: 2 }}>
        <ToggleButton value="pending" sx={{ textTransform: 'none', fontSize: '0.8rem' }}>{t('attendance:faceReviewApprovals.tabPending')}</ToggleButton>
        <ToggleButton value="approved" sx={{ textTransform: 'none', fontSize: '0.8rem' }}>{t('attendance:faceReviewApprovals.tabApproved')}</ToggleButton>
        <ToggleButton value="rejected" sx={{ textTransform: 'none', fontSize: '0.8rem' }}>{t('attendance:faceReviewApprovals.tabRejected')}</ToggleButton>
        <ToggleButton value="all" sx={{ textTransform: 'none', fontSize: '0.8rem' }}>{t('attendance:faceReviewApprovals.tabAll')}</ToggleButton>
      </ToggleButtonGroup>

      {error && <Alert severity="error" sx={{ width: '100%', mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={28} />
        </Box>
      ) : rows.length === 0 ? (
        <Paper elevation={0} sx={{ width: '100%', p: 5, textAlign: 'center', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="body2" color="text.secondary">{t('attendance:faceReviewApprovals.emptyTitle')}</Typography>
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
            {t('attendance:faceReviewApprovals.emptyHint')}
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} sx={{ width: '100%', borderRadius: 2, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
          <Table>
            <TableHead sx={{ bgcolor: 'grey.100', '& .MuiTableCell-root': { fontWeight: 600 } }}>
              <TableRow>
                <TableCell>{t('attendance:faceReviewApprovals.colWorker')}</TableCell>
                <TableCell>{t('attendance:faceReviewApprovals.colDate')}</TableCell>
                <TableCell>{t('attendance:faceReviewApprovals.colSelfie')}</TableCell>
                <TableCell>{t('attendance:faceReviewApprovals.colSimilarity')}</TableCell>
                <TableCell>{t('attendance:faceReviewApprovals.colStatus')}</TableCell>
                <TableCell align="right">{t('attendance:faceReviewApprovals.colActions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => {
                const d = (r.data ?? {}) as FaceReviewData;
                const sim = typeof d.similarity === 'number' ? `${Math.round(d.similarity)}%` : '—';
                return (
                  <TableRow key={r.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar src={r.worker?.avatar ?? undefined} sx={{ width: 28, height: 28, fontSize: '0.7rem', bgcolor: 'primary.main' }}>
                          {(r.worker?.name ?? '•').slice(0, 1)}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{r.worker?.name ?? '—'}</Typography>
                          {r.worker?.department && (
                            <Typography variant="caption" color="text.secondary">{r.worker.department}</Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>{d.date ?? '—'}</TableCell>
                    <TableCell>
                      {d.selfie_url ? (
                        <Box
                          component="img"
                          src={d.selfie_url}
                          alt={t('attendance:faceReviewApprovals.colSelfie')}
                          onClick={() => setPhotoUrl(d.selfie_url ?? null)}
                          sx={{ width: 40, height: 40, borderRadius: 1, objectFit: 'cover', cursor: 'pointer', border: '1px solid', borderColor: 'divider' }}
                        />
                      ) : '—'}
                    </TableCell>
                    <TableCell>{sim}</TableCell>
                    <TableCell>{statusChip(r.status)}</TableCell>
                    <TableCell align="right">
                      {r.status === 'pending' ? (
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Button size="small" variant="contained" color="success" disableElevation
                            disabled={acting === r.id}
                            startIcon={<CheckCircleOutlined fontSize="small" />}
                            onClick={() => decide(r.id, 'approved')}
                            sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.78rem' }}>
                            {t('attendance:faceReviewApprovals.approve')}
                          </Button>
                          <Button size="small" variant="outlined" color="error"
                            disabled={acting === r.id}
                            startIcon={<CloseOutlined fontSize="small" />}
                            onClick={() => { setRejectId(r.id); setRejectNote(''); }}
                            sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.78rem' }}>
                            {t('attendance:faceReviewApprovals.reject')}
                          </Button>
                        </Stack>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          {r.reviewed_at ? new Date(r.reviewed_at).toLocaleDateString(activeLocale()) : '—'}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <PhotoViewerDialog
        open={!!photoUrl}
        url={photoUrl}
        title={t('attendance:faceReviewApprovals.photoTitle')}
        onClose={() => setPhotoUrl(null)}
      />

      {/* Reject-with-reason */}
      <Dialog open={!!rejectId} onClose={() => setRejectId(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: '1rem', fontWeight: 600 }}>{t('attendance:faceReviewApprovals.rejectTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {t('attendance:faceReviewApprovals.rejectBody')}
          </Typography>
          <TextField fullWidth multiline minRows={2} size="small" autoFocus
            label={t('attendance:faceReviewApprovals.rejectReasonLabel')}
            value={rejectNote} onChange={(e) => setRejectNote(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectId(null)} sx={{ textTransform: 'none' }}>{t('common:actions.cancel')}</Button>
          <Button color="error" variant="contained" disableElevation
            disabled={!!acting}
            onClick={() => rejectId && decide(rejectId, 'rejected', rejectNote.trim() || undefined)}
            sx={{ textTransform: 'none', borderRadius: 1 }}>
            {t('attendance:faceReviewApprovals.reject')}
          </Button>
        </DialogActions>
      </Dialog>
        </>
      )}

      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="info" onClose={() => setToast(null)} sx={{ width: '100%' }}>{toast}</Alert>
      </Snackbar>
    </Box>
  );
};

export default FaceReviewApprovals;
